import ReportContext from './ReportContext';
import captureReportView from './captureReportView';

export default function ViewToPDF() {
    var vtp = {};

    /**
     * The entry point to render a PDF report from a view
     * @param {mvc} rootView is the view that this report is being ran from.
     * @param {Function} callback is the function that will be called when the report is complete.
     */
    vtp.renderViewToPDF = function(rootView, callback){
        if (rootView == null || rootView.drivingView == null || rootView.drivingView.Definition.ReportLayout == null){
            callback({error: 'Invalid view specified.  Make sure it is loaded, is a report and has been captured as a report.'});
            return;
        }

        var ctx =  new ReportContext(rootView, null);

        ctx.onReportStart().then(() => {
            ctx.onNewPage().then(() => {
                ctx.renderReportHeader(function() {
                    if (ctx.error != null){
                        ctx.onReportFinish().then(() => {
                            callback(ctx);
                            setTimeout(() => { ctx.cleanup(); }, 0);
                        });
                        return;
                    }
                    //Else: we can continue processing this report...EK
                    ctx.renderPage(function() {
                        if (ctx.error != null){
                            ctx.onReportFinish().then(() => {
                                callback(ctx);
                                setTimeout(() => { ctx.cleanup(); }, 0);
                            });
                            return;
                        }
                        //Else: we can continue processing this report...EK
                        ctx.renderReportFooter(function() {
                            ctx.finalizeReport(function() {
                                ctx.onReportFinish().then(() => {
                                    callback(ctx);
                                    setTimeout(() => { ctx.cleanup(); }, 0);
                                })
                            });
                        });
                    });
                });
            });
        });
    };

    /**
     * Overlays the pdf generated by a view onto an existing pdf (this operation WILL flatten target PDF and will be visual representation ONLY).
     * @param rootView - The view to generate the overlay PDF from.
     * @param targetPDF - An ArrayBuffer of a PDF document to overlay.
     * @param callback - the function that will be called when the pdf is complete.
     */
    vtp.overlayViewOnPDF = function(rootView, targetPDF, callback){
        let ctx = new ReportContext(rootView, null);

        // Create the DOM elements needed to render against and to extract the resulting PDF render...EK
        let canvas = document.createElement('canvas'),
            canvasCtx = canvas.getContext('2d'),
            jpegImg = document.createElement('img');

        let worker = async function(){
            let PDFJS = await vtp.initPDFRenderLibrary();
            let doc = await PDFJS.getDocument(targetPDF);

            for(let i = 1; i <= doc.numPages; i++){
                let page = await doc.getPage(i);

                let scale = 2;      // Set scale (zoom) level (set at 2 to improve image quality)
                let viewport = page.getViewport(scale);

                // Set dimensions to Canvas based on the viewport...EK
                canvas.height = viewport.height;
                canvas.width = viewport.width;

                // Prepare object needed by render method
                let renderContext = {
                    canvasContext: canvasCtx,
                    viewport: viewport
                };

                canvasCtx.clearRect(0, 0, canvas.width, canvas.height);   // Clear canvas for the next render...EK

                await page.render(renderContext).promise;

                //We wait for the image to be rendered, and the sections of the overlay to render on top...EK
                await new Promise((resolve) => {
                    jpegImg.onload = function() {
                        if (i > 1) {    // The pdf defaults with a page, so once we're past the first one, we need to add our own...EK
                            ctx.rootContext.pdf.addPage();
                            ctx.rootContext.ptr.top = ctx.rootContext.ptr.left = 0;
                        }

                        ctx.rootContext.pdf.addImage(jpegImg, 'jpeg', 0, 0, canvas.width * 0.66666, canvas.height * 0.66666);       // dim * 0.66666 because we scale our larger image to the size of the actual page (so it appears correctly) ... EK

                        //We need to now execute the overlay for this page (we only render the page header & footer) ...EK
                        ctx.renderPageHeader(() => {
                            //Since we're skipping the detail section, we need to move the ctx pointer to where the footer should begin...EK
                            ctx.rootContext.ptr.top = ctx.printBounds.bottom - (ctx.layout.PageFooter != null ? ctx.layout.PageFooter.height : 0);

                            ctx.renderPageFooter(() => {
                                resolve();
                            });
                        });
                    };

                    jpegImg.src = canvas.toDataURL('image/jpeg', 1);        // Push the canvas content to the img tag so we can render to the pdf...EK
                });
            }
        };

        return worker().then(() => {
            ctx.finalizeReport(() => {
                callback(ctx);
                canvas = canvasCtx = jpegImg = null;      // Clear out the temp DOM elements...EK
            })
        });
    };

    /**
     * This will convert an open view form sections into report sections to store on the ReportLayout property...EK
     * @param mvc - The view to capture.
     */
    vtp.captureReportSections = captureReportView.captureReportSections;

    /**
     * This loads the PDF.js PDF rendering library, or returns it if already loaded.
     *  !! NOTE: This (pdfjs-dist) was removed as an import and MUST be loaded via script tag otherwise it breaks google maps on iOS...EK
     *  REF: CP-243
     * @returns Promise
     */
    vtp.initPDFRenderLibrary = () => {
        if (window.pdfjsLib == null) {
            return new Promise((resolve) => {
                api.utils.loadResource('https://cdn.jsdelivr.net/npm/pdfjs-dist@2.0.943/build/pdf.min.js', 'javascript', () => {
                    resolve(window.pdfjsLib);
                });
            });
        }
        else {
            return Promise.resolve(window.pdfjsLib);
        }
    };

    return vtp;
}
